/**
 * The cookie banner.
 *
 * **There is no design for this.** The Figma file has no cookie banner - recorded in
 * docs/section-inventory.md and in M8 - so this is built from the design tokens and the
 * existing Button and Checkbox components, and is honest about being a stand-in. What
 * is *not* provisional is the behaviour, because that is the part with legal weight:
 *
 * - Nothing is loaded before the visitor answers. Not "loaded and disabled" - not
 *   loaded. See lib/analytics.ts.
 * - **Refusing is one click, exactly like accepting**, and the two buttons sit side by
 *   side with the same prominence. A "Reject" hidden behind "Settings" is the pattern
 *   the EU regulators have been fining people for.
 * - The decision can be changed afterwards, from the footer.
 * - Every word comes from the CMS, per region and per language, so each market's
 *   lawyer reviews their own text and nobody needs a deploy to change it.
 *
 * It does not trap focus. A consent banner is not a modal in law or in practice, and a
 * trap would make the page unusable for a keyboard user who wants to read the privacy
 * policy before deciding. It does take focus on appearing, which is how an assistive
 * technology user finds out it is there at all.
 */
import { useEffect, useRef, useState } from 'react';
import { AppLink } from './ui/AppLink';
import { Button } from './ui/Button';
import { Checkbox } from './ui/Field';
import { needsDecision, onOpenConsentSettings, readConsent, saveConsent } from '../lib/consent';
import type { ApiConsent } from '../lib/api';

/**
 * Nothing is handed back to the caller: saving publishes the decision through
 * `onConsentChange`, and whatever needs to react - analytics today, anything gated
 * tomorrow - subscribes to that rather than being wired through this component.
 */
export default function CookieBanner({ consent }: { consent: ApiConsent | null }) {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choices, setChoices] = useState<Record<string, boolean>>({});
  const panelRef = useRef<HTMLDivElement>(null);

  // Ask when there is no decision, or when the wording has been rewritten since the
  // last one - an old yes is not consent to new text.
  useEffect(() => {
    if (!consent) return;
    if (needsDecision(readConsent(), consent.version)) setOpen(true);
  }, [consent]);

  // The footer's "cookie settings" link reopens it, already showing the current answer.
  useEffect(() => onOpenConsentSettings(() => {
    const current = readConsent();
    setChoices({
      analytics: Boolean(current?.analytics),
      marketing: Boolean(current?.marketing)
    });
    setShowSettings(true);
    setOpen(true);
  }), []);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!consent || !open) return null;

  const decide = (analytics: boolean, marketing: boolean) => {
    saveConsent({ version: consent.version, analytics, marketing });
    setOpen(false);
    setShowSettings(false);
  };

  const has = (key: string) => (key === 'analytics' || key === 'marketing') && choices[key] === true;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6">
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-labelledby="weco-consent-heading"
        className="mx-auto flex max-w-content flex-col gap-5 rounded-m border border-grey-300 bg-surface p-6 shadow-lg focus-visible:outline-none sm:p-8"
      >
        <div className="flex flex-col gap-2">
          <h2 id="weco-consent-heading" className="text-h5 font-medium text-ink">
            {consent.heading || 'Cookies'}
          </h2>
          <p className="max-w-3xl whitespace-pre-line text-body-sm text-ink-muted">{consent.body}</p>
          {consent.policyUrl ? (
            <AppLink
              to={consent.policyUrl}
              className="w-fit text-body-sm text-brand-500 underline underline-offset-2 hover:text-brand-600"
            >
              {consent.policyLabel || consent.policyUrl}
            </AppLink>
          ) : null}
        </div>

        {showSettings ? (
          <fieldset className="flex flex-col gap-4 border-t border-grey-300 pt-5">
            <legend className="sr-only">{consent.settingsLabel}</legend>

            {/* Necessary cookies cannot be refused, so the control is shown as decided
                rather than offered as a choice that does nothing. */}
            <div className="flex flex-col gap-1">
              <Checkbox id="consent-necessary" checked disabled readOnly label={
                <span className="font-medium text-ink">{consent.necessaryTitle || 'Necessary'}</span>
              } />
              {consent.necessaryBody ? (
                <p className="pl-7 text-caption text-ink-faint">{consent.necessaryBody}</p>
              ) : null}
            </div>

            {consent.categories.map((category) => (
              <div key={category.key} className="flex flex-col gap-1">
                <Checkbox
                  id={`consent-${category.key}`}
                  checked={has(category.key)}
                  onChange={(e) => setChoices((c) => ({ ...c, [category.key]: e.target.checked }))}
                  label={<span className="font-medium text-ink">{category.title}</span>}
                />
                {category.body ? <p className="pl-7 text-caption text-ink-faint">{category.body}</p> : null}
              </div>
            ))}
          </fieldset>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          {showSettings ? (
            <Button onClick={() => decide(has('analytics'), has('marketing'))}>{consent.saveLabel}</Button>
          ) : (
            <>
              <Button onClick={() => decide(true, true)}>{consent.acceptAllLabel}</Button>
              {/* Same size, same row, same weight of decision. */}
              <Button variant="secondary" onClick={() => decide(false, false)}>{consent.rejectAllLabel}</Button>
            </>
          )}
          {!showSettings ? (
            <button
              type="button"
              onClick={() => {
                const current = readConsent();
                setChoices({ analytics: Boolean(current?.analytics), marketing: Boolean(current?.marketing) });
                setShowSettings(true);
              }}
              className="rounded-m px-2 py-1 text-body-sm text-ink-muted underline underline-offset-2 transition hover:text-ink"
            >
              {consent.settingsLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
